import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { FieldCoordinateSystem, pointToLineDistance } from '@/utils'
import { EditablePlayRenderer } from './EditablePlayRenderer'
import { ControlPointOverlay } from './ControlPointOverlay'
import { MultiDrawingControlPointOverlay } from './MultiDrawingControlPointOverlay'
import { FreehandCapture } from './FreehandCapture'
import { AlignmentGuidelinesOverlay } from './AlignmentGuidelinesOverlay'
import { PlayerSnapIndicator } from './indicators/PlayerSnapIndicator'
import { PlayersOverlay } from './PlayersOverlay'
import type { ControlPoint, Coordinate, Drawing, PathStyle } from '@/types'
import type { AlignmentSegment } from '@/utils/Alignment.utils'
import {
	deletePointFromDrawing,
	findBestPlayerSnapTarget,
	insertPointIntoDrawing,
	mergeDrawings,
} from '@/utils/Drawing.utils'
import { PLAYER_RADIUS_FEET } from '@/constants'
import { constrainToField } from '@/utils/GridSnap.utils'
import './svg-canvas.css'

// Constants
const NODE_PROXIMITY_THRESHOLD = 20

type Player = {
	id: string
	x: number
	y: number
	label: string
	color: string
}

interface SVGCanvasProps {
	width: number
	height: number
	coordSystem: FieldCoordinateSystem
	drawings: Drawing[]
	players?: Player[]
	onChange: (_drawings: Drawing[]) => void
	activeTool: 'draw' | 'select' | 'erase' | 'addPlayer' | 'fill'
	autoCorrect: boolean
	defaultStyle: PathStyle
	onDeleteDrawing?: (_id: string) => void
	onDrawingAdd?: (drawing: Drawing) => void
	eraseSize?: number
	snapThreshold: number
	selectedDrawingIds?: string[]
	zoom?: number
	panX?: number
	panY?: number
	onLinkDrawingToPlayer?: (
		_drawingId: string,
		_pointId: string,
		_playerId: string,
	) => void
	onAddPlayerAtNode?: (
		drawingId: string,
		pointId: string,
		x: number,
		y: number,
	) => void
	onPlayerLinked?: (playerId: string, position: { x: number; y: number }) => void
	onMovePlayer?: (
		playerId: string,
		x: number,
		y: number,
	) => void
	isOverCanvas?: boolean
	cursorPosition?: { x: number; y: number } | null
	onSelectionChange?: (id: string | null) => void
	onDrawingHoverChange?: (isHovered: boolean) => void
	onSelectWithPosition?: (id: string, position: { x: number; y: number }) => void
	placementMode?: { type: 'presnap'; drawingId: string } | null
	onPreSnapPlacementClick?: (
		drawingId: string,
		clickType: 'terminal' | 'path',
		point: Coordinate,
		pointId?: string,
	) => void
	onBeforeStateChange?: () => void
	alignmentSegments?: AlignmentSegment[]
	// Drawing persistence
	onDrawingUpdate?: (id: string, updates: Partial<Drawing>) => void
	// Control point persistence
	onControlPointUpdate?: (drawingId: string, pointId: string, x: number, y: number) => void
	// SVG-based player rendering (opt-in for migration)
	renderPlayersInSvg?: boolean
	onPlayerDelete?: (id: string) => void
	onPlayerFill?: (id: string) => void
	onPlayerLabelClick?: (id: string, x: number, y: number) => void
	onPlayerLinkToNode?: (playerId: string, drawingId: string, pointId: string) => void
	onPlayerHoverChange?: (id: string, isHovered: boolean) => void
	getLinemanGroup?: (blockId: string) => string[]
}

/**
 * Interactive SVG layer for drawing and editing paths.
 */
export function SVGCanvas({
							  width,
							  height,
							  coordSystem,
							  drawings,
							  isOverCanvas = false,
							  cursorPosition,
							  players,
							  onChange,
							  activeTool,
							  autoCorrect,
							  defaultStyle,
							  onDeleteDrawing,
							  onDrawingAdd,
							  eraseSize = 0,
							  snapThreshold,
							  selectedDrawingIds = [],
							  zoom = 1,
							  panX = 0,
							  panY = 0,
							  onLinkDrawingToPlayer,
							  onAddPlayerAtNode,
							  onPlayerLinked,
							  onMovePlayer,
							  onSelectionChange,
							  onDrawingHoverChange,
							  onSelectWithPosition,
							  placementMode = null,
							  onPreSnapPlacementClick,
							  onBeforeStateChange,
							  alignmentSegments = [],
							  onDrawingUpdate,
							  // SVG-based player rendering props
							  renderPlayersInSvg = false,
							  onPlayerDelete,
							  onPlayerFill,
							  onPlayerLabelClick,
							  onPlayerLinkToNode,
							  onPlayerHoverChange,
							  getLinemanGroup,
							  onControlPointUpdate,
						  }: SVGCanvasProps) {
	const [_selectedDrawingId, setSelectedDrawingId] = useState<string | null>(
		null,
	)
	const [lastDrawnDrawingId, setLastDrawnDrawingId] =
		useState<string | null>(null)
	const [drawingDragState, setDrawingDragState] = useState<{
		drawingId: string
		startFeet: Coordinate
	} | null>(null)
	const [wholeDrawingSnapTarget, setWholeDrawingSnapTarget] = useState<{
		playerId: string
		pointId: string
		playerPosition: { x: number; y: number }
	} | null>(null)

	// Refs for animation frame throttling (performance optimization)
	const dragAnimationFrameRef = useRef<number | null>(null)
	const pendingDragUpdateRef = useRef<{
		deltaX: number
		deltaY: number
		currentFeet: Coordinate
		movedDrawing: Drawing | undefined
	} | null>(null)
	const lastSnapCheckRef = useRef<number>(0)
	const SNAP_CHECK_INTERVAL = 50 // Only check snap every 50ms

	// Cleanup animation frame on unmount
	useEffect(() => {
		return () => {
			if (dragAnimationFrameRef.current) {
				cancelAnimationFrame(dragAnimationFrameRef.current)
			}
		}
	}, [])

	// Auto-clear lastDrawnDrawingId after 3 seconds
	useEffect(() => {
		if (lastDrawnDrawingId) {
			const timeout = setTimeout(() => {
				setLastDrawnDrawingId(null)
			}, 3000)

			return () => clearTimeout(timeout)
		}
	}, [lastDrawnDrawingId])

	function handleCommit(drawing: Drawing) {
		// Add to local state for immediate UI update
		onChange([...drawings, drawing])
		// Persist to server
		onDrawingAdd?.(drawing)
		setSelectedDrawingId(drawing.id)
		setLastDrawnDrawingId(drawing.id)
		onSelectionChange?.(drawing.id)
	}

	function handleSelect(id: string) {
		setSelectedDrawingId(id)
		setLastDrawnDrawingId(null)
		onSelectionChange?.(id)
	}

	function handleSelectWithDialog(id: string, position: { x: number; y: number }) {
		setSelectedDrawingId(id)
		setLastDrawnDrawingId(null)
		onSelectionChange?.(id)
		onSelectWithPosition?.(id, position)
	}

	function handlePlacementModeClick(
		drawingId: string,
		clickType: 'terminal' | 'path',
		point: Coordinate,
		pointId?: string,
	) {
		if (onPreSnapPlacementClick) {
			onPreSnapPlacementClick(drawingId, clickType, point, pointId)
		}
	}

	// Helper to update a single point position (shared logic for drag handlers)
	const updatePointPosition = useCallback((
		drawingId: string,
		pointId: string,
		x: number,
		y: number,
	) => {
		onChange(
			drawings.map((drawing) => {
				if (drawing.id !== drawingId) return drawing
				return {
					...drawing,
					points: {
						...drawing.points,
						[pointId]: {
							...drawing.points[pointId]!,
							x,
							y,
						},
					},
				}
			}),
		)
	}, [drawings, onChange])

	const handleDragStart = useCallback((
		drawingId: string,
		pointId: string,
		x: number,
		y: number,
	) => {
		// Notify parent to create history entry BEFORE drag
		onBeforeStateChange?.()
		updatePointPosition(drawingId, pointId, x, y)
	}, [onBeforeStateChange, updatePointPosition])

	const handleDragMove = useCallback((
		drawingId: string,
		pointId: string,
		x: number,
		y: number,
	) => {
		// Update drawing WITHOUT creating history entry
		updatePointPosition(drawingId, pointId, x, y)
	}, [updatePointPosition])

	const handleDragEnd = useCallback((
		drawingId: string,
		pointId: string,
		x: number,
		y: number,
	) => {
		// Update drawing with final position, no history (already captured at start)
		updatePointPosition(drawingId, pointId, x, y)
		// Persist the control point position to server
		onControlPointUpdate?.(drawingId, pointId, x, y)
	}, [updatePointPosition, onControlPointUpdate])

	const handleDragPoint = useCallback((
		drawingId: string,
		pointId: string,
		x: number,
		y: number,
	) => {
		updatePointPosition(drawingId, pointId, x, y)
	}, [updatePointPosition])

	function handleMerge(
		sourceDrawingId: string,
		sourcePointId: string,
		targetDrawingId: string,
		targetPointId: string,
	) {
		const source = drawings.find((item) => item.id === sourceDrawingId)
		const target = drawings.find((item) => item.id === targetDrawingId)

		if (!source || !target) {
			return
		}

		try {
			const merged = mergeDrawings(
				source,
				target,
				sourcePointId,
				targetPointId,
			)

			const remaining = drawings.filter(
				(item) => item.id !== sourceDrawingId && item.id !== targetDrawingId,
			)

			onChange([...remaining, merged])
			setSelectedDrawingId(merged.id)
			setLastDrawnDrawingId(null)
		} catch (error) {
			console.error('Error during merge:', error)
		}
	}

	function handleLinkToPlayer(
		drawingId: string,
		pointId: string,
		playerId: string,
	) {
		if (onLinkDrawingToPlayer) {
			onLinkDrawingToPlayer(drawingId, pointId, playerId)
		}
	}

	function handleDeletePoint(drawingId: string, pointId: string) {
		const drawing = drawings.find(d => d.id === drawingId)
		if (!drawing) return

		const updatedDrawing = deletePointFromDrawing(drawing, pointId)

		if (updatedDrawing === null) {
			// Drawing should be deleted entirely (< 2 points remain)
			onChange(drawings.filter(d => d.id !== drawingId))
			onDeleteDrawing?.(drawingId)
		} else {
			// Update the drawing with the point removed
			onChange(drawings.map(d => d.id === drawingId ? updatedDrawing : d))
			// Persist updated points and segments to server
			onDrawingUpdate?.(drawingId, { points: updatedDrawing.points, segments: updatedDrawing.segments })
		}
	}

	function handleAddPoint(drawingId: string, segmentIndex: number, position: Coordinate) {
		const drawing = drawings.find(d => d.id === drawingId)
		if (!drawing) return

		const updatedDrawing = insertPointIntoDrawing(drawing, segmentIndex, position)
		onChange(drawings.map(d => d.id === drawingId ? updatedDrawing : d))
		// Persist updated points and segments to server
		onDrawingUpdate?.(drawingId, { points: updatedDrawing.points, segments: updatedDrawing.segments })
	}

	// Store ref to overlay's path context menu handler
	const overlayPathContextMenuHandler = useRef<((
		drawingId: string,
		segmentIndex: number,
		insertPosition: Coordinate,
		pixelPosition: { x: number; y: number },
	) => void) | null>(null)

	function handlePathContextMenu(
		drawingId: string,
		segmentIndex: number,
		insertPosition: Coordinate,
		pixelPosition: { x: number; y: number },
	) {
		// Call the overlay's handler if it's available
		if (overlayPathContextMenuHandler.current) {
			overlayPathContextMenuHandler.current(drawingId, segmentIndex, insertPosition, pixelPosition)
		}
	}

	function handleDrawingDragStart(
		drawingId: string,
		feetX: number,
		feetY: number,
	) {
		setDrawingDragState({
			drawingId,
			startFeet: { x: feetX, y: feetY },
		})
		setSelectedDrawingId(drawingId)
	}

	// Apply pending drag updates in animation frame (batched, once per frame)
	const applyDragUpdate = useCallback(() => {
		const pending = pendingDragUpdateRef.current
		if (!pending || !drawingDragState) {
			dragAnimationFrameRef.current = null
			return
		}

		const { deltaX, deltaY, currentFeet, movedDrawing } = pending

		// If drawing is linked to a player, move the player too (enforce LOS exclusion)
		if (movedDrawing?.playerId && onMovePlayer && players) {
			const player = players.find((p) => p.id === movedDrawing.playerId)
			if (player) {
				const constrained = constrainToField(player.x + deltaX, player.y + deltaY)
				onMovePlayer(player.id, constrained.x, constrained.y)
			}
		}

		// Batch the drawing update
		onChange(
			drawings.map((drawing) => {
				if (drawing.id !== drawingDragState.drawingId) return drawing
				const updatedPoints: Record<string, ControlPoint> = {}
				for (const [id, point] of Object.entries(drawing.points)) {
					updatedPoints[id] = {
						...point,
						x: point.x + deltaX,
						y: point.y + deltaY,
					}
				}
				return { ...drawing, points: updatedPoints }
			}),
		)

		// Throttled snap detection (only every SNAP_CHECK_INTERVAL ms)
		const now = performance.now()
		if (now - lastSnapCheckRef.current >= SNAP_CHECK_INTERVAL) {
			lastSnapCheckRef.current = now

			if (movedDrawing?.playerId) {
				setWholeDrawingSnapTarget(null)
			} else if (movedDrawing && players && players.length > 0) {
				const updatedDrawing = {
					...movedDrawing,
					points: Object.fromEntries(
						Object.entries(movedDrawing.points).map(([id, point]) => [
							id,
							{ ...point, x: point.x + deltaX, y: point.y + deltaY },
						]),
					),
				}

				const bestTarget = findBestPlayerSnapTarget(
					updatedDrawing,
					players,
					PLAYER_RADIUS_FEET,
				)

				setWholeDrawingSnapTarget(
					bestTarget
						? {
							playerId: bestTarget.playerId,
							pointId: bestTarget.pointId,
							playerPosition: bestTarget.playerPosition,
						}
						: null,
				)
			} else {
				setWholeDrawingSnapTarget(null)
			}
		}

		setDrawingDragState({
			drawingId: drawingDragState.drawingId,
			startFeet: currentFeet,
		})

		pendingDragUpdateRef.current = null
		dragAnimationFrameRef.current = null
	}, [drawingDragState, drawings, onChange, onMovePlayer, players])

	function handleDrawingDragMove(event: React.PointerEvent<SVGSVGElement>) {
		if (!drawingDragState) return

		const rect = event.currentTarget.getBoundingClientRect()
		const screenX = event.clientX - rect.left
		const screenY = event.clientY - rect.top
		const currentFeet = coordSystem.screenToFeet(screenX, screenY, zoom || 1, panX || 0, panY || 0)
		const deltaX = currentFeet.x - drawingDragState.startFeet.x
		const deltaY = currentFeet.y - drawingDragState.startFeet.y

		const movedDrawing = drawings.find((d) => d.id === drawingDragState.drawingId)

		// Store pending update (fast, no state change)
		pendingDragUpdateRef.current = { deltaX, deltaY, currentFeet, movedDrawing }

		// Schedule animation frame if not already pending
		if (!dragAnimationFrameRef.current) {
			dragAnimationFrameRef.current = requestAnimationFrame(applyDragUpdate)
		}
	}

	function handleDrawingDragEnd(event?: React.PointerEvent<SVGSVGElement>) {
		// Link to player if snap target exists
		if (wholeDrawingSnapTarget && drawingDragState && onLinkDrawingToPlayer) {
			// CRITICAL: Move entire drawing to maintain shape before linking
			// Find the drawing and the point that will be linked
			const drawing = drawings.find((d) => d.id === drawingDragState.drawingId)
			const linkedPoint = drawing?.points[wholeDrawingSnapTarget.pointId]

			if (drawing && linkedPoint) {
				// Calculate delta from linked point's current position to player position
				const deltaX = wholeDrawingSnapTarget.playerPosition.x - linkedPoint.x
				const deltaY = wholeDrawingSnapTarget.playerPosition.y - linkedPoint.y

				// Move ALL points in the drawing by this delta to maintain shape
				const updatedPoints: Record<string, ControlPoint> = {}
				for (const [id, point] of Object.entries(drawing.points)) {
					updatedPoints[id] = {
						...point,
						x: point.x + deltaX,
						y: point.y + deltaY,
					}
				}

				// Update local state with all points moved
				onChange(
					drawings.map((d) =>
						d.id === drawing.id
							? { ...d, points: updatedPoints }
							: d,
					),
				)

				// Persist each moved control point to server
				if (onControlPointUpdate) {
					for (const [id, point] of Object.entries(updatedPoints)) {
						onControlPointUpdate(drawing.id, id, point.x, point.y)
					}
				}
			}

			// Link the point to the player (point is already at player position)
			onLinkDrawingToPlayer(
				drawingDragState.drawingId,
				wholeDrawingSnapTarget.pointId,
				wholeDrawingSnapTarget.playerId,
			)

			// Show player dialog after linking
			if (onPlayerLinked && event) {
				onPlayerLinked(wholeDrawingSnapTarget.playerId, {
					x: event.clientX,
					y: event.clientY,
				})
			}
		}
		setDrawingDragState(null)
		setWholeDrawingSnapTarget(null)
	}

	function _handleDrawingDoubleClick(
		drawingId: string,
		position: { x: number; y: number },
	) {
		onSelectWithPosition?.(drawingId, position)
	}

	return (
		<div className="svg-canvas-container">
			<svg
				width={width}
				height={height}
				className="svg-canvas"
				style={{ pointerEvents: activeTool === 'draw' ? 'none' : undefined }}
				onPointerMove={handleDrawingDragMove}
				onPointerUp={(e) => handleDrawingDragEnd(e)}
				onPointerDown={(e) => {
					if (activeTool === 'erase') {
						const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect()
						const click: Coordinate = {
							x: e.clientX - rect.left,
							y: e.clientY - rect.top,
						}
						const hit = drawings.find((drawing) => {
							// Flatten all points from segments into pixels
							const pixels = drawing.segments.flatMap((segment) =>
								segment.pointIds.map((pointId) => {
									const point = drawing.points[pointId]
									return point ? coordSystem.feetToPixels(point.x, point.y) : null
								}).filter((p): p is Coordinate => p !== null),
							)
							for (let i = 0; i < pixels.length - 1; i++) {
								const p1 = pixels[i]!
								const p2 = pixels[i + 1]!
								const dist = pointToLineDistance(click, p1, p2)
								if (dist <= eraseSize / 2) return true
							}
							return false
						})
						if (hit && onDeleteDrawing) {
							onDeleteDrawing(hit.id)
							setSelectedDrawingId((prev) => (prev === hit.id ? null : prev))
							setLastDrawnDrawingId((prev) =>
								prev === hit.id ? null : prev,
							)
						}
					} else if (activeTool === 'select' && e.target === e.currentTarget) {
						// Clicking empty space in select mode clears selection
						setSelectedDrawingId(null)
						onSelectionChange?.(null)
					}
				}}
			>
				{drawings.map((drawing) => (
					<EditablePlayRenderer
						key={drawing.id}
						drawing={drawing}
						coordSystem={coordSystem}
						onSelect={handleSelect}
						onSelectWithPosition={handleSelectWithDialog}
						isSelected={selectedDrawingIds.includes(drawing.id)}
						activeTool={activeTool}
						onDelete={onDeleteDrawing}
						onDragStart={handleDrawingDragStart}
						onHover={onDrawingHoverChange}
						onPathContextMenu={activeTool === 'select' ? handlePathContextMenu : undefined}
						zoom={zoom}
						panX={panX}
						panY={panY}
						placementMode={placementMode?.drawingId === drawing.id ? placementMode : null}
						onPlacementModeClick={handlePlacementModeClick}
					/>
				))}

				{/* Show draggable nodes for SELECT, ADD PLAYER, and ERASE tools */}
				{(activeTool === 'select' || activeTool === 'addPlayer' || activeTool === 'erase') && isOverCanvas && (
					<MultiDrawingControlPointOverlay
						drawings={drawings}
						selectedDrawingIds={selectedDrawingIds}
						players={players}
						coordSystem={coordSystem}
						snapThreshold={snapThreshold}
						cursorPosition={cursorPosition}
						proximityThreshold={NODE_PROXIMITY_THRESHOLD}
						zoom={zoom}
						panX={panX}
						panY={panY}
						onDragStart={activeTool === 'select' ? handleDragStart : undefined}
						onDragMove={activeTool === 'select' ? handleDragMove : undefined}
						onDragEnd={activeTool === 'select' ? handleDragEnd : undefined}
						onMerge={activeTool === 'select' ? handleMerge : undefined}
						onLinkToPlayer={activeTool === 'select' ? handleLinkToPlayer : undefined}
						onDeletePoint={(activeTool === 'select' || activeTool === 'erase') ? handleDeletePoint : undefined}
						onAddPoint={activeTool === 'select' ? handleAddPoint : undefined}
						onPathContextMenuHandlerRef={activeTool === 'select' ? overlayPathContextMenuHandler : undefined}
						onAddPlayerAtNode={activeTool === 'addPlayer' ? onAddPlayerAtNode : undefined}
						activeTool={activeTool}
					/>
				)}

				{/* Show draggable nodes for selected drawing when cursor is over canvas (non-SELECT tools) */}
				{activeTool !== 'select' && activeTool !== 'addPlayer' && selectedDrawingIds.length > 0 && (
					<ControlPointOverlay
						drawing={
							drawings.find((item) => selectedDrawingIds.includes(item.id)) ?? null
						}
						drawings={drawings}
						players={players}
						coordSystem={coordSystem}
						snapThreshold={snapThreshold}
						onDragPoint={handleDragPoint}
						onMerge={handleMerge}
						onLinkToPlayer={handleLinkToPlayer}
						zoom={zoom}
						panX={panX}
						panY={panY}
					/>
				)}

				{/* Alignment guidelines - appears on hover */}
				<AlignmentGuidelinesOverlay
					segments={alignmentSegments}
					coordSystem={coordSystem}
				/>

				{/* Player snap indicator during whole-drawing drag */}
				{drawingDragState && wholeDrawingSnapTarget && (
					<PlayerSnapIndicator
						coordSystem={coordSystem}
						playerPosition={wholeDrawingSnapTarget.playerPosition}
						className="svg-canvas-snap-indicator"
					/>
				)}

				{/* SVG-based player rendering (opt-in for unified rendering) */}
				{renderPlayersInSvg && players && players.length > 0 && (
					<PlayersOverlay
						players={players}
						viewBox={{ width, height }}
						coordSystem={coordSystem}
						drawings={drawings}
						currentTool={activeTool}
						zoom={zoom}
						panX={panX}
						panY={panY}
						onMove={onMovePlayer}
						onDelete={onPlayerDelete}
						onFill={onPlayerFill}
						onLabelClick={onPlayerLabelClick}
						onLinkToNode={onPlayerLinkToNode}
						onHoverChange={onPlayerHoverChange}
						getGroupedBlockIds={getLinemanGroup}
					/>
				)}
			</svg>

			<FreehandCapture
				coordSystem={coordSystem}
				style={defaultStyle}
				isActive={activeTool === 'draw'}
				autoCorrect={autoCorrect}
				onCommit={handleCommit}
				players={players}
				zoom={zoom}
				panX={panX}
				panY={panY}
			/>

			{activeTool === 'draw' && lastDrawnDrawingId && isOverCanvas && (
				<div className="svg-canvas-overlay-wrapper">
					<svg
						width={width}
						height={height}
						className="svg-canvas-overlay-svg"
					>
						<ControlPointOverlay
							drawing={
								drawings.find((item) => item.id === lastDrawnDrawingId) ?? null
							}
							drawings={drawings}
							players={players}
							coordSystem={coordSystem}
							snapThreshold={snapThreshold}
							onDragPoint={handleDragPoint}
							onMerge={handleMerge}
							onLinkToPlayer={handleLinkToPlayer}
							zoom={zoom}
							panX={panX}
							panY={panY}
						/>
					</svg>
				</div>
			)}
		</div>
	)
}

